import { CertificateField, CertificateOrientation, MERGE_FIELDS, newFieldId, pageSize } from './certificate-model';

interface TextItem {
  str: string;
  transform: number[];
  width: number;
  fontName: string;
}

interface PdfPage {
  getViewport: (options: { scale: number }) => { width: number; height: number };
  render: (options: { canvasContext: CanvasRenderingContext2D; viewport: unknown }) => { promise: Promise<void> };
  getTextContent: () => Promise<{ items: TextItem[]; styles: Record<string, { fontFamily: string }> }>;
}

interface PdfJs {
  getDocument: (options: {
    data: ArrayBuffer;
    isEvalSupported: boolean;
  }) => { promise: Promise<{ getPage: (n: number) => Promise<PdfPage> }> };
}

export interface ImportedDesign {
  background: Blob;
  orientation: CertificateOrientation;
  fields: CertificateField[];
}

interface FoundTag {
  key: string;
  xPt: number;
  baselinePt: number;
  widthPt: number;
  sizePt: number;
  family: string;
}

const TARGET_PX = 3508;
const TAG = /\{\{\s*([A-Z_]+)\s*\}\}/g;

const loadPdfJs = async (): Promise<PdfJs> => {
  await import('pdfjs-dist/legacy/build/pdf.worker.entry');
  return ((await import('pdfjs-dist/legacy/build/pdf')) as unknown) as PdfJs;
};

const findTags = (items: TextItem[], styles: Record<string, { fontFamily: string }>): FoundTag[] =>
  items.flatMap((item) => {
    const found: FoundTag[] = [];
    const sizePt = Math.hypot(item.transform[2], item.transform[3]) || 12;
    const charWidth = item.str.length ? item.width / item.str.length : 0;
    TAG.lastIndex = 0;
    let match = TAG.exec(item.str);
    while (match) {
      found.push({
        key: match[1],
        xPt: item.transform[4] + charWidth * match.index,
        baselinePt: item.transform[5],
        widthPt: charWidth * match[0].length,
        sizePt,
        family: styles[item.fontName]?.fontFamily ?? 'sans-serif',
      });
      match = TAG.exec(item.str);
    }
    return found;
  });

const toHex = (rgb: number[]) => `#${rgb.map((v) => v.toString(16).padStart(2, '0')).join('')}`;

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] ?? 255;
};

const eraseAndSample = (ctx: CanvasRenderingContext2D, box: { x: number; y: number; w: number; h: number }) => {
  const pad = Math.max(2, Math.round(box.h * 0.15));
  const x0 = Math.max(0, Math.floor(box.x - pad));
  const y0 = Math.max(0, Math.floor(box.y - pad));
  const w = Math.min(ctx.canvas.width - x0, Math.ceil(box.w + pad * 2));
  const h = Math.min(ctx.canvas.height - y0, Math.ceil(box.h + pad * 2));
  if (w <= 0 || h <= 0) return '#222222';

  const { data } = ctx.getImageData(x0, y0, w, h);
  const pixel = (x: number, y: number) => {
    const i = (y * w + x) * 4;
    return [data[i], data[i + 1], data[i + 2]];
  };

  const border: number[][] = [];
  for (let x = 0; x < w; x += 1) border.push(pixel(x, 0), pixel(x, h - 1));
  for (let y = 0; y < h; y += 1) border.push(pixel(0, y), pixel(w - 1, y));
  const background = [0, 1, 2].map((c) => median(border.map((p) => p[c])));

  let ink = [34, 34, 34];
  let darkest = Infinity;
  for (let y = pad; y < h - pad; y += 1) {
    for (let x = pad; x < w - pad; x += 1) {
      const p = pixel(x, y);
      const lum = p[0] * 0.299 + p[1] * 0.587 + p[2] * 0.114;
      if (lum < darkest) {
        darkest = lum;
        ink = p;
      }
    }
  }

  ctx.fillStyle = toHex(background);
  ctx.fillRect(x0, y0, w, h);

  return toHex(ink);
};

const fontFor = (family: string) => {
  if (/cursive|script/i.test(family)) return 'great-vibes';
  if (/serif/i.test(family) && !/sans/i.test(family)) return 'eb-garamond';
  return 'roboto';
};

export const importCertificatePdf = async (file: File): Promise<ImportedDesign> => {
  const pdfjs = await loadPdfJs();
  const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer(), isEvalSupported: false }).promise;
  const page = await pdf.getPage(1);
  const base = page.getViewport({ scale: 1 });
  const orientation: CertificateOrientation = base.width >= base.height ? 'landscape' : 'portrait';
  const scale = TARGET_PX / Math.max(base.width, base.height);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Unable to read that PDF');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;

  const { items, styles } = await page.getTextContent();
  const pageMm = pageSize(orientation);
  const mmPerPt = pageMm.width / base.width;
  const yScale = pageMm.height / base.height;

  const fields = findTags(items, styles).map((tag) => {
    const topPt = base.height - tag.baselinePt - tag.sizePt * 0.85;
    const box = {
      x: tag.xPt * scale,
      y: topPt * scale,
      w: tag.widthPt * scale,
      h: tag.sizePt * 1.15 * scale,
    };
    const ink = eraseAndSample(ctx, box);

    const tagCentreMm = (tag.xPt + tag.widthPt / 2) * mmPerPt;
    const centred = Math.abs(tagCentreMm - pageMm.width / 2) < pageMm.width * 0.06;
    const margin = 12;
    const x = centred ? margin : Math.max(0, tag.xPt * mmPerPt);
    const width = centred ? pageMm.width - margin * 2 : Math.max(20, pageMm.width - margin - x);

    const field: CertificateField = {
      id: newFieldId(),
      key: MERGE_FIELDS.includes(tag.key) ? tag.key : 'TEXT',
      text: MERGE_FIELDS.includes(tag.key) ? '' : `{{${tag.key}}}`,
      x: Math.round(x * 10) / 10,
      y: Math.round(Math.max(0, topPt * yScale - tag.sizePt * 0.05 * yScale) * 10) / 10,
      width: Math.round(width * 10) / 10,
      size: Math.round(tag.sizePt * 10) / 10,
      colour: ink,
      align: centred ? 'center' : 'left',
      font: fontFor(tag.family),
      bold: false,
    };
    return field;
  });

  const background = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Unable to read that PDF'))), 'image/png')
  );

  return { background, orientation, fields };
};
